import { useSignal } from '@preact/signals';
import { useEffect } from 'preact/hooks';

import type { AgendaEvent, EditScope, EventHistoryItem } from '@amumin/api-client';
import { t } from '@amumin/i18n';
import { Button, Dialog, Notice } from '@amumin/ui';
import { Icon, MemberChip, NEUTRAL_COLOR } from '@amumin/ui-core';

import { api } from '../api.js';
import {
  capitalize,
  conflictPeople,
  isEveryone,
  joinNames,
  membersOf,
  type MemberLite,
} from '../lib/agenda.js';
import { formatDateKeyTitle, relativeAgo } from '../lib/dates.js';
import { mutate } from '../lib/ui.js';
import { appText, messages } from '../state/session.js';
import { ConfirmDialog, ScopeDialog } from './Dialogs.js';
import { ReminderChips } from './ReminderEditor.js';
import { SheetRides } from './RideTakeover.js';
import { RowPicto } from '../lib/rowPicto.js';

// Eén afspraak in een sheet (DESIGN.md §8.W2/W4): wanneer, wie, waar, ritten met "Ik rijd",
// en de vergevingsgezinde acties (afgelasten → "Toch laten doorgaan", verwijderen → prullenbak
// met Ongedaan maken). Eén primaire knop: Bewerken.

export function EventSheet({
  occurrence,
  dayEvents,
  members,
  onClose,
  onEdit,
  onChanged,
}: {
  occurrence: AgendaEvent | null;
  /** Andere afspraken van die dag (om conflicten bij naam te noemen). */
  dayEvents: ReadonlyArray<AgendaEvent>;
  members: ReadonlyArray<MemberLite>;
  onClose: () => void;
  onEdit: (occ: AgendaEvent) => void;
  /** 0022: na "Kan niet? Iemand anders" blijft het blad open met de nieuwe stand. */
  onChanged?: (occ: AgendaEvent) => void;
}) {
  const m = messages.value;
  const a = appText.value;
  const ev = occurrence;
  const deleteOpen = useSignal(false);
  const scopeOpen = useSignal(false);
  const historyOpen = useSignal(false);
  const history = useSignal<EventHistoryItem[] | null>(null);
  const busy = useSignal(false);

  useEffect(() => {
    historyOpen.value = false;
    history.value = null;
  }, [ev?.occurrence_id]);

  if (!ev)
    return <Dialog open={false} onClose={onClose} title="" closeLabel={m.design.dialog.close} />;

  const who = membersOf(ev.member_ids, members);
  const dateLabel = formatDateKeyTitle(ev.date ?? '', m);
  const time = ev.all_day
    ? a.agenda.allDay
    : `${ev.start_hhmm ?? ''}–${ev.end_hhmm === '24:00' ? '00:00' : (ev.end_hhmm ?? '')}`;
  const others = dayEvents.filter((x) => ev.conflict_with.includes(x.occurrence_id));

  const cancelOne = async () => {
    busy.value = true;
    const res = await mutate(
      () => api.app.occurrences.cancel(ev.occurrence_id, { if_version: ev.override_version ?? 0 }),
      {
        success: t(a.event.cancelledOne, { title: ev.title }),
        undo: async () => {
          if (res) await api.app.occurrences.restore(ev.occurrence_id, res.override_version);
        },
      },
    );
    busy.value = false;
    if (res) onClose();
  };

  const restoreOne = async () => {
    busy.value = true;
    const res = await mutate(
      () => api.app.occurrences.restore(ev.occurrence_id, ev.override_version ?? 0),
      { success: t(a.event.restoredOne, { title: ev.title }) },
    );
    busy.value = false;
    if (res) onClose();
  };

  // "Terug naar de gewone afspraak" houdt een overgenomen rit en een overgeslagen herinnering
  // (API reset, DESIGN.md §8.W4a); de melding zegt wat er blijft staan.
  const resetOne = async () => {
    busy.value = true;
    const keepsRide = ev.roles.some((r) => r.source === 'override');
    const keepsReminder = (ev.skipped_reminders?.length ?? 0) > 0;
    const res = await mutate(
      () => api.app.occurrences.reset(ev.occurrence_id, ev.override_version ?? 0),
      {
        success:
          keepsRide && keepsReminder
            ? a.event.resetKeptBoth
            : keepsRide
              ? a.event.resetKeptRide
              : keepsReminder
                ? a.event.resetKeptReminder
                : a.event.resetDone,
      },
    );
    busy.value = false;
    if (res) onClose();
  };

  const remove = async (scope: EditScope) => {
    busy.value = true;
    if (scope === 'this') {
      busy.value = false;
      scopeOpen.value = false;
      await cancelOne();
      return;
    }
    const res = await mutate(
      () =>
        api.app.events.remove(
          ev.series_id,
          ev.version,
          scope === 'this_and_following'
            ? { scope, occurrence_id: ev.occurrence_id }
            : { scope: 'all' },
        ),
      {
        success: t(a.event.deleted, { title: ev.title }),
        ...(scope === 'all'
          ? {
              undo: async () => {
                if (res) await api.app.events.restore(res.id, res.version);
              },
            }
          : {}),
      },
    );
    busy.value = false;
    deleteOpen.value = false;
    scopeOpen.value = false;
    if (res) onClose();
  };

  const duplicate = async () => {
    const res = await mutate(() => api.app.events.duplicate(ev.series_id), {
      success: a.event.duplicated,
      undo: async () => {
        if (res) await api.app.events.remove(res.id, res.version);
      },
    });
    if (res) onClose();
  };

  const loadHistory = async () => {
    historyOpen.value = !historyOpen.value;
    if (historyOpen.value && !history.value) {
      try {
        history.value = (await api.app.events.history(ev.series_id)).items.slice(0, 12);
      } catch {
        history.value = [];
      }
    }
  };

  // Fijnslijpen (7 okt 2026): geen initialen achter de titel ("Zwemles · M"). In het raster
  // helpen ze; in het blad staat "Wie" er direct onder, dus dat was dubbel.
  const sheetTitle = ev.title;
  // De herhaling zonder de tijd, want die staat al op de regel erboven ("Elke woensdag", niet
  // "Elke woensdag van 21:00 tot 21:45").
  const repeatText = (() => {
    const text = capitalize(ev.recurrence_summary ?? ev.recurrence_summary_nl ?? '');
    if (!text || ev.all_day || !ev.start_hhmm || !ev.end_hhmm) return text;
    const range = t(a.recurrenceText.fromTo, {
      text: '',
      start: ev.start_hhmm,
      end: ev.end_hhmm,
    }).trim();
    const short = text
      .replace(range, '')
      .replace(/\s+,/g, ',')
      .replace(/\s{2,}/g, ' ')
      .replace(/^[,،\s]+|[,،\s]+$/g, '');
    return short || text;
  })();

  return (
    <>
      <Dialog
        open={Boolean(ev)}
        onClose={onClose}
        variant="sheet"
        title={sheetTitle}
        closeLabel={m.design.dialog.close}
        name="event-sheet"
        actions={
          <>
            {ev.cancelled ? (
              <Button name="event-restore-one" busy={busy.value} onPress={() => void restoreOne()}>
                {a.event.restoreOne}
              </Button>
            ) : null}
            <Button name="event-edit" variant="primary" icon="pencil" onPress={() => onEdit(ev)}>
              {a.event.edit}
            </Button>
          </>
        }
      >
        <div class="sheet-hero">
          <span class="sheet-hero-picto">
            <RowPicto icon={ev.icon} label={ev.title} size={56} decorative />
          </span>
          <div>
            <p class="sheet-hero-when">
              {dateLabel} · <span class="tabular nowrap">{time}</span>
            </p>
            <p class="sheet-hero-tags">
              {ev.cancelled ? <span class="tag tag-danger">{a.event.cancelled}</span> : null}
              {ev.recurring ? (
                <span class="tag">{repeatText || m.design.eventCard.repeat}</span>
              ) : null}
              {ev.moved ? (
                <span class="tag tag-info">
                  <Icon id="arrow-right-left" size={14} /> {a.event.moved}
                </span>
              ) : null}
              {ev.modified && !ev.moved ? (
                <span class="tag tag-info">{a.event.changed}</span>
              ) : null}
            </p>
          </div>
        </div>

        {ev.conflict && others.length > 0 ? (
          <Notice kind="warning" name="event-conflict">
            {others.map((o) => {
              // Alleen wie echt botst (aanwezig of rijdend), met enkel/meervoud.
              const people = conflictPeople(ev, o, members);
              if (people.length === 0) return null;
              const title = `${o.title}${o.start_hhmm ? ` (${o.start_hhmm})` : ''}`;
              const nameOf = (id: string) => members.find((x) => x.id === id)?.display_name ?? '';
              const present = people.filter((p) => !p.driving);
              const drivers = people.filter((p) => p.driving);
              return (
                <span key={o.occurrence_id} class="block">
                  {present.length > 0 ? (
                    <span class="block">
                      {t(a.event.conflictWith, {
                        count: present.length,
                        names: joinNames(present.map((p) => nameOf(p.memberId))),
                        title,
                      })}
                    </span>
                  ) : null}
                  {drivers.map((d) => (
                    <span key={d.memberId} class="block">
                      {t(a.event.conflictDrive, {
                        name: nameOf(d.memberId),
                        time: d.departHhmm ?? '',
                        title: o.title,
                      })}
                    </span>
                  ))}
                </span>
              );
            })}
          </Notice>
        ) : null}

        <dl class="detail-list">
          <div>
            <dt>{a.event.who}</dt>
            <dd class="chip-row">
              {isEveryone(ev.member_ids, members) ? (
                <MemberChip
                  name={a.common.everyone}
                  colorKey={NEUTRAL_COLOR}
                  icon="family"
                  size="sm"
                  plain
                />
              ) : (
                who.map((x) => (
                  <MemberChip
                    key={x.id}
                    name={x.display_name}
                    colorKey={x.color_key}
                    icon={x.avatar_icon}
                    size="sm"
                  />
                ))
              )}
            </dd>
          </div>
          {ev.location ? (
            <div>
              <dt>{a.event.where}</dt>
              <dd>{ev.location}</dd>
            </div>
          ) : null}
          {ev.travel ? (
            <div>
              <dt>{a.event.leave}</dt>
              <dd>
                {t(a.event.depart, { time: ev.travel.depart_hhmm })} ·{' '}
                {t(a.event.travel, { count: ev.travel.minutes })}
                {/* 0056: reistijd terug aangevinkt: "thuis om 21:30". */}
                {ev.travel.return_hhmm
                  ? ` · ${t(m.kiosk.travelBack, { time: ev.travel.return_hhmm })}`
                  : null}
              </dd>
            </div>
          ) : null}
          {ev.notes ? (
            <div>
              <dt>{a.event.notes}</dt>
              <dd class="pre-wrap">{ev.notes}</dd>
            </div>
          ) : null}
        </dl>

        <ReminderChips
          reminders={ev.reminders}
          members={members}
          occurrence={ev}
          onDone={onClose}
        />

        {/* 0022: wie rijdt er deze keer, met "Kan niet? Iemand anders" (RideTakeover.tsx). */}
        <SheetRides
          ev={ev}
          members={members}
          onDone={() => (onChanged ? onChanged(ev) : onClose())}
        />

        {/* Fijnslijpen (7 okt 2026): tekst zonder pictogram per regel of knop (zoals het
            afspraakdetail op het bord). */}
        <section class="sheet-section sheet-quiet-actions">
          {ev.recurring && !ev.cancelled ? (
            <Button
              name="event-cancel-one"
              variant="ghost"
              busy={busy.value}
              onPress={() => void cancelOne()}
            >
              {a.event.cancelOne}
            </Button>
          ) : null}
          {ev.recurring && (ev.moved || ev.modified) && !ev.cancelled ? (
            <Button name="event-reset-one" variant="ghost" onPress={() => void resetOne()}>
              {a.event.resetOne}
            </Button>
          ) : null}
          <Button name="event-duplicate" variant="ghost" onPress={() => void duplicate()}>
            {a.event.duplicate}
          </Button>
          <Button name="event-history" variant="ghost" onPress={() => void loadHistory()}>
            {a.event.history}
          </Button>
          <Button
            name="event-delete"
            variant="ghost"
            onPress={() => (ev.recurring ? (scopeOpen.value = true) : (deleteOpen.value = true))}
          >
            {a.event.delete}
          </Button>
        </section>

        {historyOpen.value ? (
          <section class="sheet-section" data-name="event-history-list">
            {!history.value ? (
              <p class="muted small">{a.common.loading}</p>
            ) : history.value.length === 0 ? (
              <p class="muted small">{a.event.historyEmpty}</p>
            ) : (
              <ul class="history-list">
                {history.value.map((h) => (
                  <li key={h.id}>
                    <span>{(a.event.action as Record<string, string>)[h.action] ?? h.action}</span>
                    <span class="muted small">
                      {t(a.event.historyItem, {
                        who: h.actor_name ?? a.common.unknown,
                        when: relativeAgo(h.created_at, m),
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}
      </Dialog>

      <ConfirmDialog
        open={deleteOpen.value}
        title={t(a.event.deleteTitle, { title: ev.title })}
        body={a.event.deleteBody}
        confirmLabel={a.event.delete}
        danger
        busy={busy.value}
        onClose={() => (deleteOpen.value = false)}
        onConfirm={() => void remove('all')}
        name="event-delete-dialog"
      />
      <ScopeDialog
        open={scopeOpen.value}
        mode="delete"
        dateLabel={dateLabel}
        busy={busy.value}
        onClose={() => (scopeOpen.value = false)}
        onChoose={(scope) => void remove(scope)}
      />
    </>
  );
}
